import { Component, computed, inject, input, signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { KENDO_MULTISELECT } from '@progress/kendo-angular-dropdowns';
import { KENDO_CHECKBOX } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { groupBy } from '@progress/kendo-data-query';
import { CommunitiesClient } from '@unity/clients';
import { ControlWrapperComponent } from '@unity/forms/composed/control-wrapper/control-wrapper.component';

@Component({
  selector: 'unity-multiselect-community-control',
  imports: [KENDO_STACKLAYOUT, KENDO_LABEL, KENDO_CHECKBOX, KENDO_MULTISELECT, ReactiveFormsModule, ControlWrapperComponent],
  templateUrl: './multiselect-community-control.component.html',
  styleUrl: './multiselect-community-control.component.scss',
})
export class MultiselectCommunityControlComponent {
  private readonly communitiesClient = inject(CommunitiesClient);

  public readonly $description = input.required<string>({ alias: 'description' });
  public readonly $control = input.required<FormControl<number[]>>({ alias: 'control' });

  public readonly $showHint = input(true, { alias: 'showHint' });

  private readonly $filter = signal<string>('');

  protected allCommunitiesResource = rxResource({
    stream: () => this.communitiesClient.getAllAsMini(1, 10000, null, 'IsActive=true'),
  });

  protected readonly $orderedCommunities = computed(() => {
    const filter = this.$filter();
    const communities = this.allCommunitiesResource.value();
    if (communities == undefined) {
      return [];
    }

    const lowerFilter = filter.toLowerCase();
    const nonStatewide = communities.data
      .filter(x => x.name.toLowerCase().includes(lowerFilter) || x.community_area_name?.toLowerCase().includes(lowerFilter))
      .sort((a, b) => {
        return (a.community_area_name ?? '').localeCompare(b.community_area_name ?? '') || a.name.localeCompare(b.name);
      })
      .map(x => ({ id: x.id, name: x.name, group: x.is_statewide ? 'All' : (x.community_area_name ?? 'Unknown Area') }));

    const areas = groupBy(nonStatewide, [
      {
        field: 'group',
        dir: 'asc',
      },
    ]);
    return [...areas];
  });

  protected readonly $statewideId = computed(() => {
    const communities = this.allCommunitiesResource.value();
    return communities?.data.find(x => x.is_statewide)?.id;
  });

  protected updateCommunities(newValues: number[]) {
    const control = untracked(this.$control);
    const statewideId = untracked(this.$statewideId);

    const currentValues = control.value;
    const isStatewide = statewideId && currentValues.includes(statewideId);
    const changeHasStatewide = statewideId && newValues.includes(statewideId);

    if (newValues.length === 1 && changeHasStatewide) {
      control.setValue(newValues);
    } else if (!isStatewide && changeHasStatewide) {
      control.setValue(newValues.filter(v => v === statewideId));
    } else {
      control.setValue(newValues.filter(v => v !== statewideId));
    }

    control.markAsTouched();
    control.markAsDirty();
  }

  protected onFilterChange(filter: string): void {
    this.$filter.set(filter);
  }
}
